import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, Warning } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Forced-change screen for accounts flagged with `must_change_password`.
 * Renders when a user created via bulk-from-HR (temporary password) lands
 * on the app the first time — closes the loop between admin-generated
 * credentials and self-owned passwords.
 */
export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error(t("cpMismatch"));
      return;
    }
    if (next.length < 8) {
      toast.error(t("cpTooShort"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("cpSuccess"));
      // Clear the flag locally so the router lets the user continue.
      setUser({ ...user, must_change_password: false });
      const perms = user?.permissions || [];
      navigate(perms.includes("dashboard.view") ? "/" : "/me", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.08)] p-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-md bg-amber-500 flex items-center justify-center">
            <HardHat size={22} weight="fill" className="text-white" />
          </div>
          <div className="leading-tight">
            <h1 className="font-heading font-black text-lg tracking-tight text-slate-900">{t("cpTitle")}</h1>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{user?.name || user?.email}</p>
          </div>
        </div>
        <div data-testid="cp-banner" className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-5 flex gap-2">
          <Warning size={16} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900">{t("cpBanner")}</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="overline-label">{t("cpCurrent")}</Label>
            <Input data-testid="cp-current" type="password" autoComplete="current-password"
                   required value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="overline-label">{t("cpNew")}</Label>
            <Input data-testid="cp-new" type="password" autoComplete="new-password"
                   required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} />
            <p className="text-[10px] text-slate-500">{t("cpHint")}</p>
          </div>
          <div className="space-y-2">
            <Label className="overline-label">{t("cpConfirm")}</Label>
            <Input data-testid="cp-confirm" type="password" autoComplete="new-password"
                   required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <Button data-testid="cp-submit" type="submit" disabled={busy}
                   className="w-full bg-blue-900 hover:bg-blue-800 font-semibold">
            {busy ? "..." : t("cpSubmit")}
          </Button>
        </form>
        <button
          data-testid="cp-logout"
          type="button"
          onClick={() => logout().then(() => navigate("/login", { replace: true }))}
          className="mt-5 w-full text-xs text-slate-500 hover:text-red-600 transition-colors"
        >
          {t("cpLogout")}
        </button>
      </div>
    </div>
  );
}
